/**
 * Social icons, hand-authored inline SVG.
 *
 * Each carries its platform's own brand colour, applied on hover so the row
 * reads as a cohesive set at rest and lights up in recognisable colours on
 * interaction. Threads is near-black, which needs to invert on dark surfaces —
 * hence the separate `onDark` colour.
 */

export interface Social {
  name: string;
  href: string;
  /** Brand colour, used on hover. */
  color: string;
  /** Override for dark surfaces, where the brand colour would vanish. */
  colorOnDark?: string;
  Icon: (p: { className?: string }) => JSX.Element;
}

const base = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true,
  focusable: 'false',
} as const;

export function LinkedInIcon({ className = '' }: { className?: string }) {
  return (
    <svg {...base} className={className}>
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm6 0h3.8v1.71h.05c.53-.95 1.83-1.96 3.76-1.96 4.02 0 4.76 2.5 4.76 5.76V21h-4v-5.63c0-1.34-.03-3.07-1.9-3.07-1.9 0-2.19 1.46-2.19 2.97V21H9V9Z" />
    </svg>
  );
}

export function FacebookIcon({ className = '' }: { className?: string }) {
  return (
    <svg {...base} className={className}>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.44 2.91h-2.34V22c4.78-.76 8.44-4.92 8.44-9.94Z" />
    </svg>
  );
}

export function InstagramIcon({ className = '' }: { className?: string }) {
  return (
    <svg {...base} className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ThreadsIcon({ className = '' }: { className?: string }) {
  return (
    <svg {...base} className={className}>
      <path d="M16.3 11.1c-.1 0-.2-.1-.3-.1-.2-3.2-1.9-5-4.8-5h-.1c-1.7 0-3.2.7-4.1 2.1l1.6 1.1c.6-1 1.6-1.2 2.5-1.2 1 0 1.8.3 2.3.9.4.4.6 1 .7 1.7-.9-.2-1.8-.2-2.8-.2-2.9.2-4.7 1.9-4.6 4.2 0 1.2.7 2.2 1.7 2.8.9.5 2 .8 3.2.7 1.6-.1 2.8-.7 3.7-1.8.6-.9 1-2 1.2-3.3.7.4 1.2 1 1.5 1.7.5 1.1.5 3-1 4.5-1.3 1.3-2.9 1.9-5.3 1.9-2.7 0-4.7-.9-6-2.6C4.6 16.9 4 14.8 4 12s.6-4.9 1.7-6.5c1.3-1.7 3.3-2.6 6-2.6 2.7 0 4.8.9 6.1 2.6.6.9 1.1 2 1.4 3.3l1.9-.5c-.3-1.6-.9-3-1.8-4.1C17.7 2.1 15.1 1 11.8 1h-.1C8.4 1 5.8 2.1 4.2 4.3 2.7 6.2 2 9 2 12s.7 5.8 2.2 7.7C5.8 21.9 8.4 23 11.7 23h.1c3 0 5-.8 6.7-2.5 2.2-2.2 2.1-4.9 1.4-6.6-.5-1.2-1.4-2.2-2.6-2.8zm-4.5 5.3c-1.3.1-2.7-.5-2.8-1.8-.1-.9.6-1.9 2.8-2.1h.6c.7 0 1.4.1 2 .2-.2 2.9-1.6 3.6-2.6 3.7z" />
    </svg>
  );
}

/**
 * Social accounts with their brand colours.
 *
 * Sourced from each platform's own brand guidelines: LinkedIn #0A66C2,
 * Facebook #1877F2, Instagram #E1306C (the magenta from its gradient — a flat
 * colour stays legible at icon size), Threads near-black #101010.
 */
export const socials: Social[] = [
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/falcon-sol-pvt-ltd',
    color: '#0A66C2',
    Icon: LinkedInIcon,
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/falconsolutions.official',
    color: '#1877F2',
    Icon: FacebookIcon,
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/falconsolutions.official',
    color: '#E1306C',
    Icon: InstagramIcon,
  },
  {
    name: 'Threads',
    href: 'https://www.threads.net/@falconsolutions.official',
    color: '#101010',
    // Threads' black would disappear on navy, so it goes white there.
    colorOnDark: '#FFFFFF',
    Icon: ThreadsIcon,
  },
];
